import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, CardContent, Typography, Button, Box, CircularProgress } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import axios from '../utils/api';

const AgencyEmailVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired' | 'already-verified'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await axios.get(`/api/service-providers/verify-email/${token}`);
        const { email, editToken } = response.data;
        if (email && editToken) {
          navigate(`/register/agency/dashboard?email=${encodeURIComponent(email)}&token=${encodeURIComponent(editToken)}`, { replace: true });
          return;
        }
        setStatus('success');
        setMessage(response.data.message || 'Email address verified successfully!');
      } catch (err: any) {
        if (err?.response?.data?.expired) {
          setStatus('expired');
          setMessage(err.response.data.message || 'The verification link has expired.');
        } else if (err?.response?.data?.verified) {
          setStatus('already-verified');
          setMessage(err.response.data.message || 'This email address is already verified.');
        } else {
          setStatus('error');
          setMessage(err?.response?.data?.message || err?.response?.data?.error || 'Failed to verify email address.');
        }
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          {status === 'loading' && (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography variant="h6">Verifying your email address...</Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom color="success.main">Email Verified!</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            </>
          )}

          {status === 'already-verified' && (
            <>
              <CheckCircle sx={{ fontSize: 80, color: 'info.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom color="info.main">Already Verified</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
              <Button variant="contained" size="large" onClick={() => navigate('/register/agency/edit-request')}>
                Access My Registration
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: 'warning.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom color="warning.main">Verification Link Expired</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
              <Button variant="contained" size="large" onClick={() => navigate('/register/agency')}>
                Return to Registration
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom color="error.main">Verification Failed</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
              <Button variant="contained" size="large" onClick={() => navigate('/register/agency')}>
                Return to Registration
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AgencyEmailVerification;
